// deps
import React, { memo, ReactElement } from "react";

//components
import { SplitPayBanner } from "../split-pay-banner";
import PaymentGroupTitle from "@payment-ui/components/payment-group/components/Title";

// helpers
import {
    getUpdatedSplitPayList,
    getFoldsInfoForSplitpayMethods,
    getSplitpayIdentifier,
} from "@payment-ui/helpers/SplitPay";
import { isEmpty } from "@swiggy-private/js-utils";
import { NewTag, useEffectOnce } from "@swiggy-private/core-ui";

// interfaces
import { SwiggyMoneyActions, SplitPayData, SplitpayAnalyticsEvents } from "@payment-ui/interfaces";

// test id
import { SPLIT_PAY_BANNER_LIST_CONTAINER_TEST_ID } from "@swiggy-private/payment-test-ids";

// styles
import { Container, NewTagWrapper, TitleContainer } from "./styles";

interface SplitPayBannerListProps {
    splitPayList: SplitPayData[];
    groupDisplayName?: string;
    handleChange: (isSplitPayChecked: boolean, methodData: SplitPayData, removeReward?: boolean) => void;
    selectedSplitpayMethods?: string;
    isFrictionLayout?: boolean;
    paymentGroupBanner?: ReactElement | null;
    addMoneyActions: SwiggyMoneyActions["addMoneyActions"];
    isAddMoneyCtaDisabled?: boolean;
    swiggyMoneyInfoActions: SwiggyMoneyActions["swiggyMoneyInfoActions"];
    onFlipkartToolTipShow?: () => void;
    handleViewAllRewardsClick?: (show: boolean) => void;
    couponApplied?: boolean;
    rewardApplied?: boolean;
    analyticsEvents?: SplitpayAnalyticsEvents;
}

function SplitPayBannerList(props: SplitPayBannerListProps): ReactElement | null {
    const {
        handleChange,
        selectedSplitpayMethods,
        splitPayList,
        isFrictionLayout = false,
        addMoneyActions,
        paymentGroupBanner,
        isAddMoneyCtaDisabled,
        swiggyMoneyInfoActions,
        groupDisplayName,
        onFlipkartToolTipShow,
        handleViewAllRewardsClick,
        couponApplied,
        rewardApplied,
        analyticsEvents,
    } = props;

    const updatedSplitPayList = getUpdatedSplitPayList(splitPayList, couponApplied, rewardApplied, isFrictionLayout);
    const { firstFoldSplitPayList, canShowViewAllRewards } = getFoldsInfoForSplitpayMethods(
        updatedSplitPayList,
        isFrictionLayout,
    );

    useEffectOnce(() => {
        if (!isEmpty(analyticsEvents?.splitpayGroupImpressionEvent)) {
            analyticsEvents?.splitpayGroupImpressionEvent();
        }
    });

    if (updatedSplitPayList.length === 0) {
        return null;
    }

    return (
        <>
            {!isEmpty(groupDisplayName) && (
                <TitleContainer>
                    <PaymentGroupTitle title={groupDisplayName} />
                    <NewTagWrapper>
                        <NewTag />
                    </NewTagWrapper>
                </TitleContainer>
            )}
            <Container data-testid={SPLIT_PAY_BANNER_LIST_CONTAINER_TEST_ID}>
                {firstFoldSplitPayList.map((data, index) => {
                    const isLastElement = index == firstFoldSplitPayList.length - 1;
                    const showViewAllRewardsCta = isLastElement && canShowViewAllRewards;
                    return (
                        <SplitPayBanner
                            data={data}
                            key={data.paymentCode}
                            isSplitPayChecked={selectedSplitpayMethods?.includes(getSplitpayIdentifier(data)) || false}
                            isFrictionLayout={isFrictionLayout}
                            handleChange={handleChange}
                            addMoneyActions={addMoneyActions}
                            paymentGroupBanner={paymentGroupBanner}
                            swiggyMoneyInfoActions={swiggyMoneyInfoActions}
                            isAddMoneyCtaDisabled={isAddMoneyCtaDisabled}
                            onFlipkartToolTipShow={onFlipkartToolTipShow}
                            showViewAllRewardsCta={showViewAllRewardsCta}
                            handleViewAllRewardsClick={handleViewAllRewardsClick}
                            analyticsEvents={analyticsEvents}
                        />
                    );
                })}
            </Container>
        </>
    );
}

export default memo(SplitPayBannerList);
